import { useState, useEffect, useCallback, useMemo, ReactNode } from 'react';
import { useAppDispatch, useAppSelector } from 'hooks';

import {
	actionSetMessage,
	actionResetMessages,
} from 'components/messages/actions.js';
import { CheckAuth } from 'pages/auth/CheckAuth';
import Header from 'components/header.js';
import Footer from 'components/footer.js';
import Container from 'react-bootstrap/Container';
import Card from 'react-bootstrap/Card';
import OPWBreadcrumb from 'components/breadcrumb.js';
import {
	actionFetchWeightDistributionData,
	WeightDistributionData,
} from './actions';
import { ClientSessionSelect } from 'components/clientSessionSelect/clientSessionSelect';

import Col from 'react-bootstrap/Col';
import Row from 'react-bootstrap/Row';
import Alert from 'react-bootstrap/Alert';
import { GenericDictSelect } from 'components/genericDictSelect.js';
import Loading from 'components/loading.js';
import RptBtns from 'components/rptBtns.js';
import { saveAs } from 'file-saver';
import SummaryTimePeriodSelect from 'components/summaryTimePeriodSelect.js';
import {
	ChartLabel,
	XAxis,
	FlexibleWidthXYPlot,
	YAxis,
	VerticalRectSeries,
	LabelSeries,
	LineSeries,
	Hint,
	DiscreteColorLegend,
} from 'react-vis';
import '../../../node_modules/react-vis/dist/style.css';
import { Session } from 'components/clientSessionSelect/sessionSelect/redux';

const TITLE = 'Weight distribution';

const BC = OPWBreadcrumb;

const yAxisTypes = {
	percentMob: '% of Mob',
	numberEid: 'Actual Numbers',
	estNumberInMob: 'Estimated Number in Mob',
};

const ParamPanel = (props: {
	setYAxis: (y: string) => void;
	handleDownload: () => void;
	session?: Session | null;
	setTimePeriod: (tp: string) => void;
	timePeriod: string;
	yAxis: string;
	resetGraph: () => void;
	weightOptions: string[];
	weightTypeIndex: number;
	setWeightTypeIndex: (i: number) => void;
}) => {
	const {
		setYAxis,
		handleDownload,
		session,
		setTimePeriod,
		timePeriod,
		yAxis,
		resetGraph,
		weightOptions,
		weightTypeIndex,
		setWeightTypeIndex,
	} = props;
	const cols = { lhs: 3, rhs: 9 };

	const yAxisSelect = !session?.UseNoIDs ? (
		<GenericDictSelect
			id="y-axis"
			label="Count to display"
			dataDict={yAxisTypes}
			cols={cols}
			controlStyle={{ width: '15em' }}
			handleChange={(evt: any) => setYAxis(evt.target.value)}
			value={yAxis}
			selectDotDotDot={false}
			loading={false}
		/>
	) : (
		"Actual numbers displayed. Not using EID's in this session."
	);

	return (
		<Card body>
			<Card.Title>{TITLE}</Card.Title>
			<Card.Body>
				<Row>
					<Col sm={4}>
						<ClientSessionSelect
							hideSessionSelect={false}
							handleBeforeSessionChange={() => resetGraph()}
						/>
					</Col>
					<Col sm={5}>
						<SummaryTimePeriodSelect
							value={timePeriod}
							required={false}
							label={'Time Period'}
							cols={cols}
							handleChange={(evt: any) => setTimePeriod(evt.target.value)}
							controlStyle={{ width: '15em' }}
							lock={false}
							className="inputRow"
						/>
						{yAxisSelect}
						<GenericDictSelect
							id="weight-type"
							label="Weight type"
							dataDict={weightOptions.reduce(
								(obj: { [key: string]: string }, w, i) => {
									obj[i] = w;
									return obj;
								},
								{}
							)}
							cols={cols}
							controlStyle={{ width: '15em' }}
							handleChange={(evt: any) => setWeightTypeIndex(evt.target.value)}
							value={weightTypeIndex}
							lock={Object.keys(weightOptions).length === 1}
						/>
					</Col>
					<Col sm={3}>
						<div className={'text-right report-buttons'}>
							<RptBtns
								enablePrint={true}
								enableHelp={false}
								enableDownload={true}
								handleDownload={() => handleDownload()}
								downloading={false}
							/>
						</div>
					</Col>
				</Row>
			</Card.Body>
		</Card>
	);
};
type Point = { x: number; y: number };
type Label = Point & {
	label: string;
};
type XLabel = Point & {
	label: string;
	yOffset: number;
};
type Rect = Point & {
	x0: number;
};
export const WeightDistribution2 = () => {
	const [data, setData] = useState<WeightDistributionData[]>([]);
	const [ticks, setTicks] = useState<string[]>([]);
	const [xlabels, setXLabels] = useState<XLabel[]>([]);
	const [histogram, setHistogram] = useState<Rect[]>([]);
	const [accum_line, setAccumLine] = useState<Point[]>([]);
	const [accum_hover, setAccumHover] = useState<Label | null>(null);

	const [loading, setLoading] = useState(false);
	const [yAxis, setYAxis] = useState('estNumberInMob');
	const session = useAppSelector((state) => state.session);
	const [timePeriod, setTimePeriod] = useState('Past 10 Days');

	const [weight_type_index, setWeightTypeIndex] = useState(0);
	const [width, setWidth] = useState<number>(window.innerWidth);

	const dispatch = useAppDispatch();

	const period = useMemo(() => {
		if (timePeriod.startsWith('Past 5')) return 'five';
		if (timePeriod.startsWith('Past 10')) return 'ten';
		return 'all';
	}, [timePeriod]);

	useEffect(() => {
		document.title = TITLE;
	}, []);

	const showMessage = useCallback(
		(data: string) => dispatch(actionSetMessage(data)),
		[dispatch]
	);

	useEffect(() => {
		const fetchData = async () => {
			if (session) {
				const resp = await actionFetchWeightDistributionData(
					session.SessionID,
					setLoading,
					showMessage,
					'raw'
				);
				setData(resp);
			}
		};
		if (session) {
			setLoading(true);
			dispatch(actionResetMessages());
			fetchData();
		} else {
			setData([]);
		}
	}, [session, showMessage, dispatch]);

	const handleDownload = async () => {
		if (data && weight_type_index < data.length) {
			const histogram = data[weight_type_index][period];
			let csv = 'weight,count\n';
			for (const weight in histogram)
				csv += weight + ',' + histogram[weight] + '\n';
			const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' });
			await saveAs(blob, 'weight_distribution.csv');
		}
	};

	useEffect(() => {
		if (data != null && weight_type_index >= data.length) setWeightTypeIndex(0);
	}, [data, weight_type_index]);

	useEffect(() => {
		if (data == null || data.length <= weight_type_index) {
			setTicks([]);
			setXLabels([]);
			setHistogram([]);
			setAccumLine([]);
			return;
		}
		const histogram = data[weight_type_index][period];
		const bin_width = data[weight_type_index].binSize;
		const total = Object.values(histogram).reduce((sum, v) => sum + v, 0);
		const ticks = Object.keys(histogram);
		const bins = ticks.map((t) => Number(t));
		ticks.push((Number(ticks.at(-1)) + bin_width).toFixed(0));
		const ymap = (count: number): number => {
			switch (yAxis) {
				case 'percentMob':
					return Math.round((1000 * count) / total) / 10;
				case 'estNumberInMob':
					if (session?.Head) return Math.round((count / total) * session?.Head);
				// else fall through
				case 'numberEid':
				default:
					return count;
			}
		};
		const rects = bins.map((w): Rect => {
			const y = ymap(histogram[w]);
			return { x0: w, x: w + bin_width, y };
		});
		const xlabels = rects.map((v): XLabel => {
			return {
				x: (v.x0 + v.x) / 2,
				y: v.y,
				label: v.y.toString() + (yAxis === 'percentMob' ? '%' : ''),
				yOffset: -5,
			};
		});
		const accum: Point[] = [];
		if (bins.length > 0) accum.push({ x: bins.at(0)!, y: ymap(total) });
		let count = total;
		bins.forEach((weight) => {
			if (histogram[weight] > 0) {
				accum.push({
					x: weight,
					y: ymap(count),
				});
				count -= histogram[weight];
			}
		});
		if (bins.length > 0) accum.push({ x: bins.at(-1)! + bin_width, y: 0 });

		setTicks(ticks);
		setXLabels(xlabels);
		setHistogram(rects);
		setAccumLine(accum);
	}, [data, yAxis, session, timePeriod, weight_type_index]);
	const dist_domain = [0, histogram.reduce((m, v) => Math.max(v.y, m), 0)];
	const accum_domain =
		accum_line.length < 2 ? [] : [accum_line.at(-1)!.y, accum_line.at(0)!.y];
	const resetGraph = () => {
		setData([]);
	};
	const ylabel = useMemo(() => {
		if (yAxis === 'estNumberInMob') return 'Estimated number in mob';
		if (yAxis === 'percentMob') return 'Percentage of mob';
		return 'Number of EIDs';
	}, [yAxis]);
	const heading = useMemo(() => {
		if (timePeriod === '') return 'Select period';
		if (loading) return 'Loading data...';
		return 'No data for this session.';
	}, [timePeriod, loading]);

	function handleWindowSizeChange() {
		setWidth(window.innerWidth);
	}
	useEffect(() => {
		window.addEventListener('resize', handleWindowSizeChange);
		return () => {
			window.removeEventListener('resize', handleWindowSizeChange);
		};
	}, []);
	const is_narrow = width < 768;

	const masterProps = {
		setYAxis,
		handleDownload,
		session,
		setTimePeriod,
		timePeriod,
		yAxis,
		yAxisTypes,
		resetGraph,
		weightOptions: data?.map((row) => row.weightType) || [],
		weightTypeIndex: weight_type_index,
		setWeightTypeIndex: setWeightTypeIndex,
	};

	return (
		<Template>
			<ParamPanel {...masterProps} />
			{ticks.length === 0 ? (
				<Alert
					className={'text-center'}
					variant={'secondary'}
					style={{ backgroundColor: 'rgb(233,236,239)' }}
				>
					<div className="w-50 mx-auto">
						<Loading loading={loading} />
					</div>
					<h2>{heading}</h2>
				</Alert>
			) : (
				<>
					<h2 className={'only-print'}>Optiweigh - Weight distribution</h2>
					<Card body>
						<div className="text-center">
							<Loading loading={loading} />
						</div>
						<FlexibleWidthXYPlot
							height={400}
							margin={
								is_narrow
									? { left: 36, right: 36, top: 30, bottom: 60 }
									: { left: 70, right: 70, top: 30, bottom: 60 }
							}
							onMouseLeave={() => {
								setAccumHover(null);
							}}
						>
							<VerticalRectSeries
								data={histogram}
								yDomain={dist_domain}
								color="#2a7ab3"
								stroke="rgb(21,105,167)"
							/>
							<LineSeries
								data={accum_line}
								yDomain={accum_domain}
								color="#2ca02c"
								onNearestX={(point: Point) => {
									setAccumHover(
										point.y > 0
											? {
													...point,
													label: point.y + (yAxis === 'percentMob' ? '%' : ''),
												}
											: null
									);
								}}
							/>
							<LabelSeries
								data={xlabels}
								yDomain={dist_domain}
								labelAnchorX="middle"
								labelAnchorY="bottom"
								style={{ fill: '#2a7ab3' }}
							/>
							{accum_hover && (
								<Hint
									value={accum_hover}
									align={{ horizontal: 'right', vertical: 'auto' }}
								>
									<div
										style={{
											color: '#2ca02c',
											background: 'white',
											border: '1px solid grey',
											borderRadius: '4px',
											padding: '4px 8px',
										}}
									>
										{accum_hover.label}
									</div>
								</Hint>
							)}
							<XAxis
								position="middle"
								tickValues={ticks}
								style={{ line: { stroke: 'black' } }}
							/>
							<YAxis
								yDomain={dist_domain}
								style={{ line: { stroke: 'rgb(21,105,167)' } }}
							/>
							<YAxis
								orientation="right"
								position="middle"
								style={{ line: { stroke: '#2ca02c' } }}
								yDomain={accum_domain}
							/>
							<ChartLabel
								text={`Weight (${data && weight_type_index < data.length && data[weight_type_index].units})`}
								className="alt-x-label"
								xPercent={0.5}
								yPercent={1}
								style={{
									transform: 'translate(0,-35)',
									textAnchor: 'middle',
								}}
							/>
							{!is_narrow && (
								<ChartLabel
									text={ylabel}
									className="alt-y-label"
									xPercent={0}
									yPercent={0.4}
									style={{
										transform: 'rotate(-90) translate(0,25)',
										textAnchor: 'middle',
									}}
								/>
							)}
							{!is_narrow && (
								<ChartLabel
									text={ylabel}
									className="alt-y-label"
									xPercent={1}
									yPercent={0.4}
									style={{
										transform: 'rotate(-90) translate(0,-20)',
										textAnchor: 'middle',
									}}
								/>
							)}
						</FlexibleWidthXYPlot>
						<DiscreteColorLegend
							style={{ float: 'right' }}
							orientation="horizontal"
							items={[
								{ title: 'Total number at weight or above', color: '#2ca02c' },
							]}
						/>
					</Card>
				</>
			)}
		</Template>
	);
};

const Template = (props: { children: ReactNode }) => (
	<>
		<Header>
			<BC trail='[["Home", "/"], ["reports", "/"], ["weight distribution", ""]]' />
		</Header>
		<Container fluid className="body">
			<CheckAuth groupName={'user'} />
			{props.children}
		</Container>
		<Container fluid>
			<Row>
				<Col sm="12">
					<Footer />
				</Col>
			</Row>
		</Container>
	</>
);
